import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  IOgpoVtsCars,
  IOgpoVtsClients,
  IOgpoVtsState,
  IOgpoVtsInsurant,
  IOgpoVtsInsured,
} from '../types';
import { IOgpoDetail } from '../../interfaces/calculators';

const initialState: IOgpoVtsState = {
  clients: [],
  cars: [],
  insurant: null,
  insureds: [],
  phoneNumber: '',
  otp: '',
  draftInfo: {
    discount: 0,
    discountedPremium: 0,
    id: '',
    premium: 0,
    promotions: [],
    relatedDealBool: false,
    relatedDealPremium: 0,
    relatedDealComission: 0,
    totalPremium: 0,
  },
};

const ogpoVtsSlice = createSlice({
  name: 'ogpoVts',
  initialState,
  reducers: {
    reset: () => initialState,

    addClients: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<IOgpoVtsClients>
    ) => {
      state.clients.push(action.payload); // добавляет новые элементы в конец массива.
    },

    addInsurant: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<IOgpoVtsInsurant>
    ) => {
      state.insurant = action.payload; // добавляет нового страхователя
    },

    deleteInsurant: (state: Draft<IOgpoVtsState>) => {
      state.insurant = null; // удаляет страхователя
    },

    addInsured: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<IOgpoVtsInsured>
    ) => {
      // добавляет нового застрахованного
      state.insureds.push(action.payload);
    },

    deleteInsuredById: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<number>
    ) => {
      // удалить застрахованного по id
      const newInsuredsArray = state.insureds.filter(
        (element) => Number(element.id) !== Number(action.payload)
      );

      state.insureds = newInsuredsArray;
    },

    deleteClientById: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<number>
    ) => {
      // удалить клиента со списка
      const newClientsArray = state.clients.filter(
        (element) => Number(element.id) !== Number(action.payload)
      );

      /// при удаление какого либо водителя делаю первого в списке основным
      if (newClientsArray.length !== 0) {
        newClientsArray[0].coreDriver = true;
      }

      if (newClientsArray.length === 1) {
        newClientsArray[0].notDriver = false;
      }

      state.clients = newClientsArray;
    },

    changeClientById: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<{ id: number; newData: IOgpoVtsClients }>
    ) => {
      // изменить данные клиента
      const newClientsArray = state.clients.map((element) => {
        if (Number(element.id) === Number(action.payload.id)) {
          return action.payload.newData;
        }
        return element;
      });

      state.clients = newClientsArray;
    },

    changeInsurantByIin: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<{ iin: string; newData: IOgpoVtsInsurant }>
    ) => {
      // изменить данные страхователя
      if (state.insurant && state.insurant.iin === action.payload.iin) {
        state.insurant = action.payload.newData;
      }
    },

    changeInsuredByIin: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<{ iin: string; newData: IOgpoVtsInsured }>
    ) => {
      // изменить данные застрахованного
      const newInsuredsArray = state.insureds.map((element) => {
        if (element.iin === action.payload.iin) {
          return action.payload.newData;
        }
        return element;
      });

      state.insureds = newInsuredsArray;
    },

    setNotDriver: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<number>
    ) => {
      // установить водителя как не основного
      const newClentsArray = state.clients.map((element) => {
        if (Number(element.id) === Number(action.payload)) {
          element.notDriver = !element.notDriver;
        }
        return element;
      });

      state.clients = newClentsArray;
    },

    addCars: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<IOgpoVtsCars>
    ) => {
      state.cars.push(action.payload);
    },

    resetCars: (state: Draft<IOgpoVtsState>) => {
      state.cars = [];
    },

    deleteCarById: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<number>
    ) => {
      const newCarsArray = state.cars.filter(
        (element) => Number(element.id) !== Number(action.payload)
      );
      state.cars = newCarsArray;
    },

    changeCarById: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<{ id: number; newData: IOgpoVtsCars }>
    ) => {
      const newCarsArray = state.cars.map((element) => {
        if (Number(element.id) === Number(action.payload.id)) {
          return action.payload.newData;
        }
        return element;
      });

      state.cars = newCarsArray;
    },

    changeCarByStateNumber: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<{ stateNumber: string; newData: IOgpoVtsCars }>
    ) => {
      const newCarsArray = state.cars.map((element) => {
        if (element.car_number === action.payload.stateNumber) {
          return action.payload.newData;
        }
        return element;
      });

      state.cars = newCarsArray;
    },

    addPhoneNumber: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<string>
    ) => {
      state.phoneNumber = action.payload;
    },

    addOTP: (state: Draft<IOgpoVtsState>, action: PayloadAction<string>) => {
      state.otp = action.payload;
    },

    addDraftInfo: (
      state: Draft<IOgpoVtsState>,
      action: PayloadAction<IOgpoDetail>
    ) => {
      state.draftInfo = action.payload;
    },
  },
});

export const {
  addClients,
  addInsurant,
  deleteInsurant,
  addInsured,
  deleteInsuredById,
  addCars,
  resetCars,
  deleteCarById,
  changeCarById,
  deleteClientById,
  changeClientById,
  changeInsuredByIin,
  changeInsurantByIin,
  changeCarByStateNumber,
  addPhoneNumber,
  addDraftInfo,
  reset,
  addOTP,
  setNotDriver,
} = ogpoVtsSlice.actions;
export default ogpoVtsSlice.reducer;
